import { useCallback, useRef, useMemo, ReactElement } from 'react';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { Flex } from '@cad-arc';
import { EventPanelAdditionalInfos } from '../additional-infos/event-panel-additional-infos/event-panel-additional-infos';
import { useCurrentUser } from '../current-user/current-user';
import { useEventMetadataStore } from '../event-panel/event-panel-form/event-metadata';
import { agencyTypeDisplayNames } from '../common/helpers/agency-type';
import { AgencyTypeGlobal } from '../common/global-attributes';
import { observer } from 'mobx-react-lite';
import { useCadNextAdditionalInfoNotifications } from '../event-notifications/use-additional-info-notifications';
import { usePrevious } from '../common/hooks/use-previous';
import { useApplicationType } from '../common/hooks/use-application-type';
import { logError } from '../monitoring/log';

export type EventNarrativeProps = {
    eventId?: number;
    onOpenEvent?: (id: number) => void;
    isPanelFocused?: boolean;
    isReadOnlyMode?: boolean;
};

export const EventNarrativeContent = observer(function EventNarrativeContent({
    eventId,
    onOpenEvent,
    isPanelFocused,
    isReadOnlyMode,
}: EventNarrativeProps): ReactElement {
    const eventMetadataStore = useEventMetadataStore();
    const metadata = eventMetadataStore.state.metadata;
    const agencyEventId = eventId ?? metadata?.eventId;
    const previousIsScheduledEvent = usePrevious(metadata?.isScheduledEvent);
    const focusInputRef = useRef<() => void>();
    const notificationManager = useCadNextAdditionalInfoNotifications();
    const { isFirstResponder } = useApplicationType();
    const { userId } = useCurrentUser();

    const crossClassInformation = useMemo(() => {
        const agencyTypeGlobalAttrId = metadata?.agencyTypeGlobalAttrId;
        const hasCrossClass = eventMetadataStore.hasCrossClassEvents;
        const crossClassEvents = metadata?.linkedAndCrossClassEvents?.filter(
            (eventInfoDto) => !eventInfoDto.isLinkedEvent
        );

        if (agencyTypeGlobalAttrId && hasCrossClass && crossClassEvents) {
            const crossClassOption =
                agencyTypeDisplayNames[agencyTypeGlobalAttrId as AgencyTypeGlobal];

            return {
                agencyType: agencyTypeGlobalAttrId,
                crossClassOption,
                crossClassOptions: [
                    ...crossClassEvents.map((eventInfoDto) => ({
                        label:
                            agencyTypeDisplayNames[
                                eventInfoDto?.agencyTypeGlobalAttrId as AgencyTypeGlobal
                            ],
                        value: eventInfoDto?.agencyTypeGlobalAttrId,
                    })),
                    { label: crossClassOption, value: agencyTypeGlobalAttrId },
                ].sort((a, b) => a.label.localeCompare(b.label)),
            };
        }
    }, [
        eventMetadataStore.hasCrossClassEvents,
        metadata?.agencyTypeGlobalAttrId,
        metadata?.linkedAndCrossClassEvents,
    ]);

    const onLexicalError = useCallback(({ error, agencyEventId, message }) => {
        logError(error, {
            eventId: agencyEventId,
            message,
        });
    }, []);

    if (agencyEventId === undefined || metadata?.agencyEventNumber === undefined) {
        return <LoadingIndicator />;
    }

    return (
        <Flex
            flex="1 1 auto"
            flexDirection="column"
            position="relative"
            overflowY="hidden"
            height="100%"
            data-testid="fr-event-narrative"
        >
            <EventPanelAdditionalInfos
                agencyEventId={agencyEventId}
                forceReload={previousIsScheduledEvent && !metadata?.isScheduledEvent}
                agencyEventNumber={metadata.agencyEventNumber}
                crossClassInformation={crossClassInformation}
                currentUserId={userId}
                // TODO replace vehicles
                dispatchReplaceVehicles={() => {}}
                focusInputRef={focusInputRef}
                isPanelFocused={isPanelFocused}
                isScheduledEvent={metadata.isScheduledEvent}
                onLexicalError={onLexicalError}
                onOpenEvent={onOpenEvent}
                notificationManager={notificationManager}
                isFirstResponder={isFirstResponder}
                isReadOnlyMode={isReadOnlyMode}
            />
        </Flex>
    );
});
